import React, {useState} from 'react';
import {Button, Form, Input, message, Modal, Table, theme, Upload, UploadFile, UploadProps} from "antd";
import {ColumnsType} from "antd/es/table";
import {QueryKey, useQueryClient} from "@tanstack/react-query";

import {useCommonUseMutation, useCommonUseQuery} from "../../../../../Common/hooks/rest/ReacQueryHook";
import {procAdminService, procMultipartService} from "../../../../../Common/util/ProcService";
import {TB_CLIENT_RELEASE} from "./data/TB_CLIENT_RELEASE";
import {EditOutlined, UploadOutlined} from "@ant-design/icons";
import useModal, {ModalProps} from "../../../../../Common/hooks/modal/useModal";
import {RcFile} from "antd/es/upload";
import LoadingContainer from "../../../../../Common/component/LoadingContainer/LoadingContainer";
import {ReleaseClientFileData} from "./data/ReleaseClientFileData";
import {bytesToFileSizeString} from "../../../../../Common/util/FileUtil";

const ReleaseMgr: React.FC = () => {
    const { token: { colorBgContainer },} = theme.useToken();

    const [regModal, setRegModal] = useState<boolean>(false);
    const [releaseNote, setReleaseNote] = useState<string>("");
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [showDeleteBox, setShowDeleteBox] = useState<boolean>(false);
    const [showDetailBox, setShowDetailBox] = useState<boolean>(false);
    const [releaseDetailData, setReleaseDetailData] = useState<ReleaseClientFileData[]>([]);
    //모달창 가져오기
    const { openModal } = useModal();

    const queryClient = useQueryClient();
    const queryKey:QueryKey =   [`ReleaseMgr`];
    const serviceCode:string       =   process.env.REACT_APP_SERVICE_NAME as string;

    const mutateUpdate = useCommonUseMutation<any>(queryKey, queryClient
        , procMultipartService);

    const mutateDelete = useCommonUseMutation<any>(queryKey, queryClient
        , procAdminService);

    const { data, isFetching, refetch} = useCommonUseQuery<TB_CLIENT_RELEASE[]>(
        queryKey,
        () =>procAdminService<TB_CLIENT_RELEASE[]>("post", `${serviceCode}CTS03`, null));

    const onSearch = (value: string) => {
        refetch();
    }

    function onRegiModal()
    {
        setRegModal(true);
    }

    function onCancelModal()
    {
        setRegModal(false);
    }

    function onCancelDetailModal()
    {
        setShowDetailBox(false);
    }

    const onDelete = (record:TB_CLIENT_RELEASE) => {

        Modal.confirm({
            title: `${record.releaseNote}(Ver.${record.releaseVersion}) 배포 파일을 삭제하시겠습니까?`,
            content: "삭제 후에는 복구되지 않습니다.",
            open: showDeleteBox,
            okText: "확인",
            cancelText: "취소",
            onOk() {
                setShowDeleteBox(false);

                const deleteReleaseParam : TB_CLIENT_RELEASE =            {
                    releaseId : record.releaseId,
                    releaseVersion : record.releaseVersion,
                    releaseNote : record.releaseNote
                }

                const updateParam = { method: 'post', serviceCode: `${serviceCode}CTD01`, requestBody:deleteReleaseParam }
                return mutateDelete.mutateAsync(updateParam)
                    .catch(error => {
                        console.error(error);

                    }).finally(() => {
                    });
            },
            onCancel() {
                setShowDeleteBox(false);
            },
        });

    }

    const onShowFile = (record:TB_CLIENT_RELEASE) => {

        const fileShowParam : TB_CLIENT_RELEASE =            {
            releaseId : record.releaseId,
            releaseVersion : record.releaseVersion,
            releaseNote : record.releaseNote
        }

        setShowDetailBox(true);
        procAdminService<ReleaseClientFileData[]>("post", `${serviceCode}CTS04`, fileShowParam).then(rev =>{
            setReleaseDetailData(rev);
        });
    }

    function onSaveRelease()
    {
        if (fileList.length !== 1)
        {
            const modalProps:ModalProps = {
                title : "배포파일을 첨부해야 합니다.",
                type : "warning",
                content : `배포파일을 첨부해야 합니다.`

            }
            openModal(modalProps);

            return;
        }
        else
        {
            setRegModal(false);
            setIsUploading(true);

            const formData = new FormData();
            fileList.forEach((file) => {
                formData.append('multipartFiles', file as RcFile);
            });


            const requestBody = {
                releaseNote:releaseNote
            }

            formData.append('requestBody', new Blob([JSON.stringify(requestBody)], { type: 'application/json' }));

            const updateParam = { method: 'post', serviceCode: `${serviceCode}CTC01`, requestBody:formData }
            return mutateUpdate.mutateAsync(updateParam)
                .catch(error => {
                    console.error(error);
                    message.error('upload failed.');
                }).then(() => {
                    setFileList([]);
                    message.success('upload successfully.');
                }).finally(() => {
                    setFileList([]);
                    setIsUploading(false);

                });

        }
    }

    const onChangeReleaseNote = (e:any) => {
        setReleaseNote(e.target.value);
    }

    const columns: ColumnsType<TB_CLIENT_RELEASE> = [
        {
            title: 'Release Version',
            dataIndex: 'releaseVersion',
            key: 'releaseVersion',
            render: (text: string) =>
                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Version {text}
                </div>
            ,
        },
        {
            title: 'Release Note',
            dataIndex: 'releaseNote',
            key: 'releaseNote',
            render: (text: string) =>
                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {text}
                </div>
            ,
        },
        {
            title: '',
            key: 'releaseId',
            dataIndex: 'Active',
            render: (releaseId, record:TB_CLIENT_RELEASE) =>
                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

                    <Button type="link" size="small" onClick={()=>{
                        setShowDetailBox(true);
                        onShowFile(record);
                    }}>파일보기</Button>
                    <Button type="link" size="small" onClick={()=>{
                        onDelete(record);
                    }}>삭제</Button>

                </div>
            ,
        },
    ];


    const uploadProps: UploadProps = {
        onRemove: (file) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
        },
        accept : ".zip",
        beforeUpload: (file) => {
            if (fileList.length === 0)
            {
                setFileList([...fileList, file]);
            }
            else {
                const modalProps:ModalProps = {
                    title : "배포파일은 한개만 첨부 가능합니다.",
                    type : "warning",
                    content : `배포파일은 한개만 첨부 가능합니다.`

                }
                openModal(modalProps);
            }

            return false;
        },
        fileList,
    };


    return (
        <>
        <nav style={{
            margin:'0px 0px 14px 0px',
            padding:'9px 8px 9px 0px',
            height: '50px',
            display:'flex',
            justifyContent: "space-between",
            alignItems:'center',
            borderRadius: "8px", // 라운드 처리 추가
            border: '1px solid #dadada',
            background: colorBgContainer,
        }}>
            <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{
                    padding: '9px 5px',
                    display:'flex',
                }}>
                </div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
                <Button style={{marginRight:"10px"}} type={"primary"} onClick={onRegiModal}>배포등록</Button>
            </div>
        </nav>
        <Table columns={columns}
               dataSource={data}
               rowKey="releaseId"
               loading={isFetching}
        />

            <Modal
                open={regModal}
                title="배포파일 등록"
                onCancel={onCancelModal}
                footer={[
                    <></>
                ]}
            >
                <Form
                    name="saveBatch"
                    initialValues={{
                        remember: false,
                    }}
                    onFinish={onSaveRelease}
                >
                    <Form.Item
                        label={"배포내용"}
                        name="ReleaseNote"
                        rules={[
                            {
                                required : true,
                            },
                        ]}
                    >
                        {
                                <Input
                                    prefix={<EditOutlined className="site-form-item-icon" />}
                                    placeholder="배포내용"
                                    value={releaseNote}
                                    onChange={onChangeReleaseNote}
                                />
                        }

                    </Form.Item>
                    <Form.Item
                        label={"배포파일"}
                        name="ReleaseFiles"
                        rules={[
                            {
                                required : true,
                            },
                        ]}
                    >
                        {
                            <Upload {...uploadProps} >
                                <Button icon={<UploadOutlined />}>Select File</Button>
                            </Upload>
                        }
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" className="login-form-button">
                            적용
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
            {
                releaseDetailData &&
                <Modal
                    width={1000}
                    open={showDetailBox}
                    title="배포파일 목록"
                    onCancel={onCancelDetailModal}
                    footer={[
                        <></>
                    ]}
                >
                    <Table columns={[
                        {
                            title: 'Ver',
                            dataIndex: 'fileVersion',
                            key: 'fileVersion',
                            render: (text: string) =>
                                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {text}
                                </div>
                            ,
                        },
                        {
                            title: '파일명',
                            dataIndex: 'fileName',
                            key: 'fileName',
                            render: (text: string) =>
                                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'left', justifyContent: 'left' }}>
                                    {text}
                                </div>
                            ,
                        },
                        {
                            title: '설치경로',
                            key: 'releasePath',
                            dataIndex: 'releasePath',
                            render: (text) =>
                                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'left', justifyContent: 'left' }}>
                                    {text}
                                </div>
                            ,
                        },
                        {
                            title: '파일크기',
                            key: 'fileSize',
                            dataIndex: 'fileSize',
                            render: (text) =>
                                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'right', justifyContent: 'right' }}>
                                    {bytesToFileSizeString(text)}
                                </div>
                            ,
                        },

                    ]}
                           dataSource={releaseDetailData}
                           rowKey="releaseId"
                    />
                </Modal>
            }

            {isUploading && <LoadingContainer message={"배포 파일 업로드 중.."}/> }
        </>
    );
};

export default ReleaseMgr;
